/**
 * Web Lab, split into the three files a real project has.
 *
 * Every authored Web Lab scaffold is ONE self-contained document with an inline
 * <style> and an inline <script> (see starterTemplate/authoredLessons.js), and for
 * the build-chain lessons that is the right shape: it is exactly the index.html the
 * student downloads and keeps.
 *
 * CD04's Module 3 is the exception. Those three lessons — V4301 HTML, V4302 CSS,
 * V4303 JavaScript — exist to teach that a page is made of three DIFFERENT
 * languages, and a student who only ever sees them stacked inside one file has to
 * take that on faith. Splitting them across index.html / style.css / script.js and
 * putting a tab on each makes the module's own claim visible: the tab strip IS the
 * lesson. It also matches what they will open in Claude Code two modules later,
 * where a project is a folder of files rather than a single page.
 *
 * WHY SPLIT HERE AND NOT IN THE SCAFFOLD. The scaffolds are aliased, not copied —
 * V4301/V4302/V4303 ARE CD03's V3302/V3303/V3304, and CD03 is live and single-file.
 * Authoring a second, three-file set of the same pages would be exactly the drift
 * authoredLessons.js was written to prevent: two copies of a scaffold and its
 * matched checks is two chances for them to disagree. So the ONE document stays the
 * source of truth and this module derives the three files from it, per lesson, from
 * a `multiFile` flag on the registry entry. Nothing about grading changes: the
 * preview still runs one joined document, so the same DOM checks grade both shapes.
 *
 * The split is REVERSIBLE and the round trip is what the layout relies on — it
 * hands `joinWebLabFiles` output back up as `editorCode`, so the preview harness,
 * the Ask-Claude context, the download and the parent's `setCurrentCode` all keep
 * seeing an ordinary complete page and need no idea any of this happened.
 *
 * Joining is not optional, either: the preview iframe is sandboxed with no network
 * and an opaque origin, so `href="style.css"` resolves to nothing. The files must be
 * inlined back into one document before it can run at all.
 */

/**
 * The three files, in tab order. `key` is the field on a split object; `name` is
 * shown to the student and is also the literal filename referenced inside
 * index.html, so the two cannot drift.
 *
 * Not localized on purpose. These are filenames, like the ids and class names in
 * the scaffolds — the student types them into a real project later.
 */
export const WEB_LAB_FILES = [
    { key: "html", name: "index.html", language: "html" },
    { key: "css", name: "style.css", language: "css" },
    { key: "js", name: "script.js", language: "javascript" },
];

export const DEFAULT_WEB_LAB_FILE = "html";

// The blocks lifted out of the document. `<script>` matches only an INLINE one:
// the negative lookahead skips anything with a `src`, which is what stops the tag
// this module itself writes from being lifted out again on a second pass.
//
// Fresh RegExp per call rather than shared constants: these are global, and a
// global regex carries `lastIndex` between uses.
const styleBlocks = () => /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi;
const inlineScriptBlocks = () => /<script\b(?![^>]*\bsrc\s*=)[^>]*>([\s\S]*?)<\/script\s*>/gi;

// What replaces them. A leading `./` is tolerated when reading back so a student
// who "corrects" the path doesn't lose their stylesheet.
const LINK_TAG = '<link rel="stylesheet" href="style.css" />';
const SCRIPT_TAG = '<script src="script.js"></script>';

const LINK_REF = /<link\b[^>]*\bhref\s*=\s*["']\.?\/?style\.css["'][^>]*>/i;
const SCRIPT_REF = /<script\b[^>]*\bsrc\s*=\s*["']\.?\/?script\.js["'][^>]*>\s*<\/script\s*>/i;

// The scaffolds write `<style>\n  body { … }\n</style>`, so the lifted text opens on
// a newline and closes on the indentation of the closing tag. Both would show as
// stray blank lines at the top and bottom of a freshly opened file.
const tidy = (text) => String(text == null ? "" : text).replace(/^\n+/, "").replace(/\s+$/, "");

/**
 * Pull every matching block out of the document into one file, leaving a single
 * reference tag behind at position `keep` and deleting the others.
 *
 * MOST of Module 3 is GENERATED, not authored — only V4301/V4302/V4303 (aliased from
 * CD03) and V4308 (CD02's V2307) have hand-written scaffolds. The generator is told
 * to emit "inline <style> and <script>", singular, and usually does, but nothing
 * enforces it and the definitions are cached per lesson in Firestore, so starters
 * that predate this file cannot be re-inspected. Taking only the first block would
 * leave the second one stranded in index.html, and then the tab strip would be
 * lying: the student would edit style.css and watch a rule in index.html override it.
 *
 * WHERE THE SURVIVING TAG GOES is different for the two, and it matters:
 *
 *   CSS  → the FIRST block's position. Merging in document order preserves the
 *          cascade, and there is no other stylesheet for the position to matter
 *          against, so this is safe wherever it lands.
 *   JS   → the LAST block's position. Merging preserves execution order between the
 *          blocks, but the merged block itself must not run EARLIER than any code
 *          that was in it — a <head> script that ran before the body was parsed and
 *          a bottom-of-body script that ran after cannot both keep their timing.
 *          Landing on the last position only ever moves code later, which is the
 *          harmless direction: inline `onclick` handlers fire on interaction, and
 *          later scripts see a more complete DOM, not a less complete one.
 */
const liftBlocks = (doc, makeRegex, keep, tag) => {
    const bodies = [];
    // Function replacers throughout: student CSS and JS are free text, and `$&`,
    // `` $` ``, `$'` and `$$` are all special in a string replacement.
    doc.replace(makeRegex(), (match, body) => {
        bodies.push(tidy(body));
        return match;
    });

    if (bodies.length === 0) return { doc, body: "" };

    const target = keep === "last" ? bodies.length - 1 : 0;
    let seen = -1;
    const next = doc.replace(makeRegex(), () => {
        seen += 1;
        return seen === target ? tag : "";
    });

    // `filter(Boolean)` so a scaffold's empty `<script></script>` yields an empty
    // file rather than a run of blank lines — but the reference tag still goes in,
    // because index.html should show the wiring even before anything is written.
    return { doc: next, body: bodies.filter(Boolean).join("\n\n") };
};

/**
 * Take one self-contained page apart into { html, css, js }.
 *
 * A missing <style> or <script> is not an error — the file comes back empty and the
 * student can type into it, and `joinWebLabFiles` will place what they write.
 *
 * @param {string} html A complete document.
 * @returns {{html: string, css: string, js: string}}
 */
export const splitWebLabFiles = (html) => {
    const source = typeof html === "string" ? html : "";

    const styles = liftBlocks(source, styleBlocks, "first", LINK_TAG);
    const scripts = liftBlocks(styles.doc, inlineScriptBlocks, "last", SCRIPT_TAG);

    return { html: scripts.doc, css: styles.body, js: scripts.body };
};

/**
 * Put the three files back together into the single document the preview runs.
 *
 * Each file goes back where its reference is, so the page keeps the shape the
 * student is reading in the index.html tab. If the reference is gone — a student
 * deleted the <link> while editing — the content is placed at the end of <head> or
 * <body> instead rather than silently vanishing: these lessons never ask anyone to
 * touch those two tags, so a blank page with no explanation would be a dead end
 * rather than a lesson.
 *
 * @param {{html?: string, css?: string, js?: string}} files
 * @returns {string} A complete document.
 */
export const joinWebLabFiles = (files) => {
    const html = files && typeof files.html === "string" ? files.html : "";
    const css = files && typeof files.css === "string" ? files.css : "";
    const js = files && typeof files.js === "string" ? files.js : "";

    let doc = html;

    const styleBlock = `<style>\n${css}\n</style>`;
    if (LINK_REF.test(doc)) {
        doc = doc.replace(LINK_REF, () => styleBlock);
    } else if (css.trim()) {
        if (/<\/head\s*>/i.test(doc)) doc = doc.replace(/<\/head\s*>/i, () => `${styleBlock}\n</head>`);
        else doc = `${styleBlock}\n${doc}`;
    }

    const scriptBlock = `<script>\n${js}\n</script>`;
    if (SCRIPT_REF.test(doc)) {
        doc = doc.replace(SCRIPT_REF, () => scriptBlock);
    } else if (js.trim()) {
        if (/<\/body\s*>/i.test(doc)) doc = doc.replace(/<\/body\s*>/i, () => `${scriptBlock}\n</body>`);
        else doc = `${doc}\n${scriptBlock}`;
    }

    return doc;
};

/** The three files as fenced blocks, for the Ask-Claude context. */
export const describeWebLabFiles = (files) =>
    WEB_LAB_FILES.map(({ key, name, language }) => {
        const body = (files && files[key]) || "";
        return `${name}:\n\n\`\`\`${language === "javascript" ? "js" : language}\n${body || "(empty)"}\n\`\`\``;
    }).join("\n\n");

export default splitWebLabFiles;
